/**
 * MessageRow — A single message row in the inbox list.
 * Shows channel icon, sender, preview, timestamp, urgency badge, read status.
 */
import { Mail, MessageSquare, Phone, Send, Hash } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import type { InboxMessage } from "@/types/inbox";

const channelIcons: Record<string, React.ReactNode> = {
  slack: <Hash className="h-4 w-4" />,
  email: <Mail className="h-4 w-4" />,
  sms: <Phone className="h-4 w-4" />,
  telegram: <Send className="h-4 w-4" />,
  whatsapp: <MessageSquare className="h-4 w-4" />,
};

const urgencyVariant: Record<string, "danger" | "warning" | "default"> = {
  blocking: "danger",
  clarification: "warning",
  advisory: "default",
};

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface MessageRowProps {
  message: InboxMessage;
  isSelected: boolean;
  onClick: () => void;
}

export function MessageRow({ message, isSelected, onClick }: MessageRowProps) {
  const icon = channelIcons[message.channelType] ?? <Mail className="h-4 w-4" />;
  const variant = urgencyVariant[message.urgency] ?? "default";
  const preview = message.content.length > 100
    ? message.content.slice(0, 100) + "…"
    : message.content;

  return (
    <button
      type="button"
      data-testid={`message-row-${message.id}`}
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
        isSelected
          ? "border-primary/50 bg-primary/5"
          : "border-transparent hover:bg-white/[0.02]",
        !message.isRead && "bg-white/[0.03]",
      )}
    >
      {/* Unread dot */}
      <div className="mt-1.5 flex shrink-0 items-center">
        {!message.isRead && (
          <div className="h-2 w-2 rounded-full bg-primary" data-testid="unread-dot" />
        )}
        {message.isRead && <div className="h-2 w-2" />}
      </div>

      {/* Channel icon */}
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-raised text-muted">
        {icon}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className={cn("truncate text-sm", !message.isRead ? "font-semibold text-white" : "text-foreground")}>
            {message.senderName}
          </span>
          <span className="shrink-0 text-xs text-muted">
            {formatRelativeTime(message.timestamp)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">{preview}</p>
        <div className="mt-1.5 flex items-center gap-2">
          <Badge variant={variant} className="text-[10px]">
            {message.urgency}
          </Badge>
          <span className="text-[10px] uppercase text-muted">{message.channelType}</span>
        </div>
      </div>
    </button>
  );
}
